import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { Microservices } from "../../app/AllowedMicroservices";
import { RootState } from "../../app/store";
import apiActionCreator, { HttpMethods } from "../../services/apiActionCreator";
import { FetchStatus } from "../common/enums";

// Mirrors the response of GET /progress-audit/progress/:progressId/full
// (service-learning, SuperAdmin only): course + topics + lessons + the raw
// Progress document for one progressId, regardless of who's logged in.
// Meant to feed the "copiar contexto completo" debugging button on the
// Progress Audit screen - fields are left loose (raw lean mongoose docs)
// since this is only ever JSON.stringify'd, never rendered field by field.
export interface IProgressAuditFullContext {
  course: Record<string, unknown> | null;
  topics: Record<string, unknown>[];
  lessons: Record<string, unknown>[];
  progress: Record<string, unknown> | null;
}

interface ProgressAuditFullState {
  data: IProgressAuditFullContext | null;
  status: FetchStatus;
}

const initialState: ProgressAuditFullState = {
  data: null,
  status: FetchStatus.Idle,
};

export const progressAuditFullSlice = createSlice({
  name: "progressAuditFull",
  initialState,
  reducers: {
    fetching: (state) => {
      state.status = FetchStatus.Fetching;
    },
    fetched: (state, action: PayloadAction<IProgressAuditFullContext>) => {
      state.data = action.payload || null;
      state.status = FetchStatus.Fetched;
    },
    error: (state) => {
      state.data = null;
      state.status = FetchStatus.Error;
    },
    clear: (state) => {
      state.data = null;
      state.status = FetchStatus.Idle;
    },
  },
});

export const {
  fetching,
  fetched,
  error,
  clear: clearProgressAuditFull,
} = progressAuditFullSlice.actions;

export const fetchProgressAuditFullByProgressId = (progressId: string) => (dispatch: Dispatch<object>) => {
  return dispatch(
    apiActionCreator({
      endpoint: `/progress-audit/progress/${progressId}/full`,
      types: {
        requestType: fetching,
        successTypes: [
          {
            actionOrCreator: fetched,
          },
        ],
        errorTypes: [
          {
            actionOrCreator: error,
          },
        ],
      },
      method: HttpMethods.GET,
      microservice: Microservices.LEARNING_SERVICE,
      authenticated: true,
    })
  );
};

export const selectProgressAuditFull = (state: RootState) => state.progressAuditFull.data;
export const selectHasProgressAuditFullError = (state: RootState) =>
  state.progressAuditFull.status === FetchStatus.Error;
export const selectIsFetchingProgressAuditFull = (state: RootState) =>
  state.progressAuditFull.status === FetchStatus.Fetching;

export default progressAuditFullSlice.reducer;
